import { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Artifact } from '@yingbaba/types';
import { Button, ErrorNotice, Field, Modal, Select } from '@yingbaba/ui';
import { useConfirmLeave, useFormDraft } from './UnsavedChanges';
import { briefFromForm, BriefInputs } from './StructuredBrief';
import { useArtifactCreate } from './artifact-command';

export const creativeKinds = [
  ['brief', '创作需求'],
  ['script', '视频脚本'],
  ['storyboard', '分镜脚本'],
  ['copy', '营销文案'],
  ['meeting_actions', '会议行动项'],
] as const;

export function CreativeDraftDialog({
  ai,
  onClose,
  onCreated,
}: {
  ai: boolean;
  onClose: () => void;
  onCreated: (artifact: Artifact) => void;
}) {
  const id = useId();
  const [kind, setKind] = useState('brief');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [completed, setCompleted] = useState<Artifact>();
  const pending = useRef(false);
  const saved = useRef<Artifact | undefined>(undefined);
  const createdCallback = useRef(onCreated);
  createdCallback.current = onCreated;
  const create = useArtifactCreate();
  const qc = useQueryClient();
  const leave = useConfirmLeave();
  const draft = useFormDraft({ enabled: true, busy, save, discard: onClose });
  useEffect(() => {
    if (completed) createdCallback.current(completed);
  }, [completed]);

  async function save(form: HTMLFormElement): Promise<boolean> {
    if (pending.current || !form.reportValidity()) return false;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const fields = Object.fromEntries(new FormData(form));
      saved.current = await create({
        title: fields.title,
        kind: fields.kind,
        input: fields.input,
        ...(fields.kind === 'brief' ? { brief: briefFromForm(fields) } : {}),
      });
      draft.clean();
      await qc.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith('/workspace/artifacts'),
      });
      return true;
    } catch (reason) {
      setError(reason);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      title="开始新的创作"
      description="写下创作背景和目标，生成后可继续编辑、保存版本。"
      busy={busy}
      onOpenChange={(open) => {
        if (!open) leave(onClose);
      }}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => leave(onClose)}>
            取消
          </Button>
          <Button form={id} type="submit" loading={busy}>
            {ai ? '开始生成' : '建立创作草稿'}
          </Button>
        </>
      }
    >
      <form
        id={id}
        ref={draft.ref}
        className="form-stack"
        onChange={draft.onChange}
        onSubmit={async (event) => {
          event.preventDefault();
          if (await save(event.currentTarget)) setCompleted(saved.current);
        }}
      >
        <ErrorNotice error={error} />
        <Field label="创作标题" htmlFor={`${id}-title`}>
          <input
            className="input"
            id={`${id}-title`}
            name="title"
            required
            minLength={2}
            maxLength={100}
            placeholder="例如：秋日品牌形象片"
          />
        </Field>
        <Field label="创作类型" htmlFor={`${id}-kind`}>
          <Select id={`${id}-kind`} name="kind" value={kind} onChange={setKind}>
            {creativeKinds
              .filter(([value]) => value !== 'meeting_actions')
              .map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="说说你的想法" htmlFor={`${id}-input`}>
          <textarea
            id={`${id}-input`}
            name="input"
            required
            minLength={10}
            maxLength={8000}
            rows={5}
            placeholder="品牌背景、目标受众、画面风格、成片时长…"
          />
        </Field>
        {kind === 'brief' && (
          <details className="creative-brief-details">
            <summary>补充结构化需求（可选）</summary>
            <div className="form-stack">
              <BriefInputs idPrefix={`${id}-brief`} />
            </div>
          </details>
        )}
      </form>
    </Modal>
  );
}
